// Workforce Framework Engine API client (workforce-framework-service, behind
// the gateway at /api/v1/*). Covers the Evaluation Workspace surface:
// frameworks (to pick a published one), evaluation cycles, subjects, the
// scoring/submission flow, and the score → explanation ("show the why") result.
//
// docs/workforce-framework/frontend.md §Evaluation Workspace + api-events.md.
import { gatewayApi } from '@/lib/hrApi';
import { isEntitlementUnavailable } from '@/lib/apiError';

// ------------------------------------------------------- employee directory
// Evaluation subjects are opaque references (no employee PII lives in the
// workforce service). This search only helps the UI fill the reference + label
// from the tenant's employee directory; only subject_ref + label are ever sent.
export interface EmployeeHit {
  id: string; // employee_id — the directory's employee NUMBER when present (opaque subject ref), else the uuid
  employee_uuid?: string; // the employee record uuid — what work/assignment APIs mean by employee_id
  user_id?: string; // auth user id (present when the employee has a login) — needed for recognition
  name: string;
  sublabel?: string; // position / department for disambiguation
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const searchEmployees = async (q: string): Promise<EmployeeHit[]> => {
  const term = q.trim();
  if (term.length < 1) return [];
  const { data } = await gatewayApi.get<any[]>('/api/v1/employees/search', {
    params: { q: term, limit: 10 },
  });
  return (data || []).map((e) => {
    const id = String(e.employee_id ?? e.id ?? '');
    const rawUuid = String(e.id ?? '');
    const employee_uuid = UUID_RE.test(rawUuid) ? rawUuid : undefined;
    const user_id = e.user_id ? String(e.user_id) : undefined;
    const name = [e.first_name, e.last_name].filter(Boolean).join(' ').trim() || id;
    const sublabel = e.position || e.job_title || e.department || '';
    return { id, employee_uuid, user_id, name, sublabel };
  });
};

// ---------------------------------------------------------------- frameworks
export interface Framework {
  id: string;
  name: string;
  category?: string;
  status: string; // draft | active | published | archived …
  visibility?: string;
  current_version?: string | number | null;
}

export interface FrameworkLevel {
  key: string;
  label: string;
  score?: number;
  anchor_text?: string | null;
}

export interface FrameworkFactor {
  key: string;
  label: string;
  levels: FrameworkLevel[];
}

export interface FrameworkGrade {
  code: string;
  label: string;
}

export interface FrameworkCompetency {
  key: string;
  label: string;
  description?: string;
}

export interface FrameworkDefinition {
  factors: FrameworkFactor[];
  grades: FrameworkGrade[];
  competencies?: FrameworkCompetency[];
}

export interface FrameworkDetail {
  framework: Framework;
  definition: FrameworkDefinition;
}

const BASE = '/api/v1';

export const listFrameworks = async (): Promise<Framework[]> => {
  const { data } = await gatewayApi.get<{ frameworks: Framework[] }>(`${BASE}/frameworks`);
  return data.frameworks || [];
};

// createFramework builds a new framework from a fully-formed Definition (e.g. one
// assembled from an imported grading worksheet). The backend validates the
// Definition (factors, weight-sum, levels) and returns 400 with details if invalid.
export interface CreateFrameworkInput {
  name: string;
  description?: string;
  category?: string;
  industry?: string;
  region?: string;
  visibility?: string; // private | shared | marketplace
  license?: Record<string, unknown>;
  definition: FrameworkDefinition;
  supported_employee_types?: string[];
}
// aiFrameworkFromWorksheet asks Dana (ai-agent) to interpret an ARBITRARY grading /
// competency worksheet and return a proposed framework Definition + metadata for
// the user to review before creating. Handles layouts the deterministic importer
// can't (point-factor, transposed, Swahili headers).
export interface AiFrameworkResult {
  name: string;
  category: string;
  industry: string;
  definition: FrameworkDefinition;
  notes: string;
  confidence: number;
}
export const aiFrameworkFromWorksheet = async (payload: {
  name?: string;
  headers: string[];
  sample_rows: Record<string, string>[];
  total_rows: number;
}): Promise<AiFrameworkResult> => {
  const { data } = await gatewayApi.post<AiFrameworkResult>('/api/v1/ai/framework-from-worksheet', payload);
  return data;
};

export const createFramework = async (input: CreateFrameworkInput): Promise<Framework> => {
  const { data } = await gatewayApi.post<any>(`${BASE}/frameworks`, {
    visibility: 'private',
    license: {},
    supported_employee_types: [],
    ...input,
  });
  return (data.framework ?? data) as Framework;
};

export const getFramework = async (id: string): Promise<FrameworkDetail> => {
  const { data } = await gatewayApi.get<FrameworkDetail>(`${BASE}/frameworks/${id}`);
  return data;
};

// ---------------------------------------------------------- evaluation cycles
export interface EvaluationCycle {
  id: string;
  name: string;
  framework_id: string;
  framework_version_id?: string;
  status: string; // draft | active | closed …
  created_at?: string;
}

export interface EvaluationSubject {
  id: string;
  subject_ref: string;
  label: string;
  status: string; // not_started | in_progress | pending_approval | approved | locked | reopened
}

export interface CycleDetail {
  cycle: EvaluationCycle;
  subjects: EvaluationSubject[];
}

export const listCycles = async (status?: string): Promise<EvaluationCycle[]> => {
  const { data } = await gatewayApi.get<{ cycles: EvaluationCycle[] }>(
    `${BASE}/evaluation-cycles`,
    { params: status ? { status } : undefined },
  );
  return data.cycles || [];
};

export const createCycle = async (input: {
  name: string;
  framework_id: string;
  self_due?: string;
  manager_due?: string;
}): Promise<EvaluationCycle> => {
  const { data } = await gatewayApi.post<any>(`${BASE}/evaluation-cycles`, input);
  return (data.cycle ?? data) as EvaluationCycle;
};

export const getCycle = async (id: string): Promise<CycleDetail> => {
  const { data } = await gatewayApi.get<CycleDetail>(`${BASE}/evaluation-cycles/${id}`);
  return data;
};

export const startCycle = async (id: string): Promise<EvaluationCycle> => {
  const { data } = await gatewayApi.post<any>(`${BASE}/evaluation-cycles/${id}/start`);
  return (data.cycle ?? data) as EvaluationCycle;
};

export const addSubject = async (
  cycleId: string,
  input: { subject_ref: string; label: string },
): Promise<EvaluationSubject> => {
  const { data } = await gatewayApi.post<any>(`${BASE}/evaluation-cycles/${cycleId}/subjects`, {
    subject_type: 'employee',
    subject_ref: input.subject_ref,
    label: input.label,
  });
  return (data.subject ?? data) as EvaluationSubject;
};

export const assignEvaluator = async (input: {
  subject_id: string;
  evaluator_user_id: string;
  role?: string;
  factor_keys?: string[];
}): Promise<void> => {
  await gatewayApi.post(`${BASE}/evaluation-cycles/assign`, {
    role: 'manager',
    factor_keys: [],
    ...input,
  });
};

/** Submit one factor response. component_key = factor key; selected_level = a
 *  level key from that factor. */
export const submitResponse = async (input: {
  subject_id: string;
  component_key: string;
  selected_level: string;
  comment?: string;
}): Promise<void> => {
  await gatewayApi.post(`${BASE}/evaluation-cycles/submit`, input);
};

// ---------------------------------------------------------- scoring + results
export interface ScoreResult {
  id: string;
  subject_id: string;
  total_score: number;
  grade_code: string;
  comp_impact?: any;
  status: string; // draft | approved | locked …
}

export interface FactorContribution {
  key: string;
  level: string;
  raw: number;
  weight: number;
  weighted: number;
}

export interface RuleFired {
  set?: string;
  explain: string;
  // The rule's `then` outcome — an object from the engine (e.g. {grade:"M3"}),
  // not a string. Render via a formatter, never directly as a React child.
  result?: Record<string, unknown> | string;
}

export interface Explanation {
  total_score: number;
  factor_contributions: FactorContribution[];
  rules_fired: RuleFired[];
  grade?: string;
  [k: string]: any;
}

export interface OverrideEntry {
  field: string;
  from?: string | number | null;
  to: string | number;
  reason: string;
  by?: string;
  at?: string;
}

export interface SubjectDetail {
  subject: EvaluationSubject;
  result?: ScoreResult;
  explanation?: Explanation;
  overrides?: OverrideEntry[];
}

export const getSubject = async (sid: string): Promise<SubjectDetail> => {
  const { data } = await gatewayApi.get<SubjectDetail>(
    `${BASE}/evaluation-cycles/subjects/${sid}`,
  );
  return data;
};

/** Run the scoring engine — returns the draft result. */
export const scoreSubject = async (sid: string): Promise<ScoreResult> => {
  const { data } = await gatewayApi.post<any>(
    `${BASE}/evaluation-cycles/subjects/${sid}/score`,
  );
  return (data.result ?? data) as ScoreResult;
};

/** Approve a scored subject. Throws 409 when the scorer tries to approve their
 *  own work (segregation of duties). */
export const approveSubject = async (sid: string): Promise<ScoreResult> => {
  const { data } = await gatewayApi.post<any>(
    `${BASE}/evaluation-cycles/subjects/${sid}/approve`,
  );
  return (data.result ?? data) as ScoreResult;
};

export const lockSubject = async (sid: string): Promise<ScoreResult> => {
  const { data } = await gatewayApi.post<any>(
    `${BASE}/evaluation-cycles/subjects/${sid}/lock`,
  );
  return (data.result ?? data) as ScoreResult;
};

export const reopenSubject = async (sid: string, reason: string): Promise<void> => {
  await gatewayApi.post(`${BASE}/evaluation-cycles/subjects/${sid}/reopen`, { reason });
};

export const overrideGrade = async (input: {
  subject_id: string;
  to_value: string;
  reason: string;
}): Promise<ScoreResult> => {
  const { data } = await gatewayApi.post<any>(`${BASE}/evaluation-cycles/override`, {
    field: 'grade',
    ...input,
  });
  return (data.result ?? data) as ScoreResult;
};

// ------------------------------------------------------- Phase 3: simulation
export interface SimSubject {
  ref: string;
  inputs: { factors: { factor_key: string; selected_level?: string; numeric_value?: number }[] };
  current_pay?: number;
}

export interface BandPlacement {
  grade_code: string;
  currency?: string;
  min?: number;
  mid?: number;
  max?: number;
  current_pay?: number;
  compa_ratio?: number;
  position: string; // below_min | in_band | above_max | no_band
  aligned?: boolean;
  comp_impact?: boolean;
}

export interface SimulationResult {
  results: { ref: string; result: Explanation & { grade?: string }; band_placement?: BandPlacement }[];
  grade_distribution: Record<string, number>;
  band_distribution: Record<string, number>;
  comp_impact_count: number;
  promotion_ready: { ref: string; grade: string; reason?: string }[];
  count: number;
  validation?: any;
}

/** Dry-run the interpreter over sample subjects (never persisted). When a subject
 *  carries current_pay and the caller has comp rights, band placement is included. */
export const simulateFramework = async (
  frameworkId: string,
  subjects: SimSubject[],
): Promise<SimulationResult> => {
  const { data } = await gatewayApi.post<SimulationResult>(
    `${BASE}/frameworks/${frameworkId}/simulate`,
    { subjects },
  );
  return data;
};

// -------------------------------------------- Phase 3: succession / write-back
export interface SuccessionRow {
  subject_id: string;
  subject_ref: string;
  label?: string;
  grade_code?: string;
  promotion_ready: boolean;
  promotion_reason?: string;
  comp_impact: boolean;
  band_aligned?: boolean | null;
  status: string;
}

export const getSuccession = async (cycleId?: string): Promise<SuccessionRow[]> => {
  const { data } = await gatewayApi.get<{ pool: SuccessionRow[] }>(
    `${BASE}/evaluation-cycles/succession`,
    { params: cycleId ? { cycle_id: cycleId } : undefined },
  );
  return data.pool || [];
};

export interface GradeProposal {
  status: string; // pending_approval | applied
  change_request_id?: string;
}

/** Propose the locked grade as the employee's career_level via employee-service's
 *  maker-checker. Requires workforce:comp_impact. */
export const applyGrade = async (sid: string): Promise<GradeProposal> => {
  const { data } = await gatewayApi.post<GradeProposal>(
    `${BASE}/evaluation-cycles/subjects/${sid}/apply-grade`,
  );
  return data;
};

// ------------------------------------------------------- Phase 5: marketplace
export interface Listing {
  id: string;
  framework_id: string;
  owner_tenant_id: string;
  title: string;
  summary?: string;
  category?: string;
  industry?: string;
  region?: string;
  status: string; // draft | submitted | listed | suspended
  featured: boolean;
  rating_avg: number;
  rating_count: number;
  license_type?: string; // free | one_time | subscription | revenue_share
  price_amount?: number | null;
  price_currency?: string;
  billing_period?: string;
  created_at?: string;
}

export const browseMarketplace = async (category?: string, industry?: string): Promise<Listing[]> => {
  const { data } = await gatewayApi.get<{ listings: Listing[] }>(`${BASE}/marketplace`, {
    params: { ...(category ? { category } : {}), ...(industry ? { industry } : {}) },
  });
  return data.listings || [];
};

export const submitListing = async (input: {
  framework_id: string;
  title: string;
  summary?: string;
  category?: string;
  industry?: string;
  region?: string;
  license_type?: string;
  price_amount?: number;
  price_currency?: string;
  billing_period?: string;
}): Promise<Listing> => {
  const { data } = await gatewayApi.post<Listing>(`${BASE}/marketplace/listings`, input);
  return data;
};

export const installListing = async (id: string): Promise<{ framework: Framework; listing_id: string }> => {
  const { data } = await gatewayApi.post<any>(`${BASE}/marketplace/${id}/install`);
  return data;
};

export const reviewListing = async (id: string, rating: number, comment?: string): Promise<void> => {
  await gatewayApi.post(`${BASE}/marketplace/${id}/reviews`, { rating, comment });
};

// Sharing (tenant → tenant)
export const shareFramework = async (
  frameworkId: string,
  granteeTenantId: string,
  license?: Record<string, unknown>,
): Promise<void> => {
  await gatewayApi.post(`${BASE}/frameworks/${frameworkId}/share`, {
    grantee_tenant_id: granteeTenantId,
    license,
  });
};

export interface ShareRow {
  id: string;
  framework_id: string;
  grantee_tenant_id: string;
  created_at?: string;
}
export const listShares = async (frameworkId: string): Promise<ShareRow[]> => {
  const { data } = await gatewayApi.get<{ shares: ShareRow[] }>(`${BASE}/frameworks/${frameworkId}/shares`);
  return data.shares || [];
};
export const revokeShare = async (frameworkId: string, grantee: string): Promise<void> => {
  await gatewayApi.delete(`${BASE}/frameworks/${frameworkId}/share/${grantee}`);
};

// Platform operator verification (used by admin-portal; here for completeness)
export const pendingListings = async (): Promise<Listing[]> => {
  const { data } = await gatewayApi.get<{ listings: Listing[] }>(`${BASE}/marketplace/pending`);
  return data.listings || [];
};
export const verifyListing = async (id: string): Promise<Listing> => {
  const { data } = await gatewayApi.post<Listing>(`${BASE}/marketplace/${id}/verify`);
  return data;
};
export const suspendListing = async (id: string, reason?: string): Promise<Listing> => {
  const { data } = await gatewayApi.post<Listing>(`${BASE}/marketplace/${id}/suspend`, { reason });
  return data;
};

/** True when the workforce backend isn't routed/deployed to this workspace yet
 *  (gateway 404/50x family) — surfaces an empty state, not an error.
 *  ⚠️ Not the gateway's 503 entitlement fallback: that is "try again"
 *  (isEntitlementUnavailable), not a rollout. */
export const isNotRolledOut = (e: any): boolean => {
  if (isEntitlementUnavailable(e)) return false;
  const s = e?.response?.status;
  return s === 404 || s === 501 || s === 502 || s === 503 || s === 504;
};

/** True when the backend rejected the action because the same reviewer scored
 *  and is trying to approve (segregation of duties). */
export const isSodConflict = (e: any): boolean => e?.response?.status === 409;
